import { useState, useMemo, useRef, type ComponentProps } from "react"
import { startOfDay, endOfDay, startOfMonth, isBefore, isAfter } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import type { DateRangeMode, DateRangeValue } from "./dateRangePicker.types"
import {
  applyDateRangePreset,
  type DateRangePreset,
} from "./dateRangePicker.utils"
import { DateRangePickerProvider } from "./dateRangePicker.context"
import DateRangePickerSidebar from "./DateRangePickerSidebar"
import DateRangePickerBody from "./DateRangePickerBody"
import DateRangePickerInputs from "./DateRangePickerInputs"
import DateRangePickerFooter from "./DateRangePickerFooter"

const DEFAULT_HEIGHT = 460
const MIN_HEIGHT = 300

function laterDate(a: Date | undefined, b: Date): Date {
  if (!a) return b
  return isAfter(a, b) ? a : b
}

function earlierDate(a: Date | undefined, b: Date): Date {
  if (!a) return b
  return isBefore(a, b) ? a : b
}

export type DateRangePickerProps = ComponentProps<"div"> & {
  value?: DateRangeValue
  onApply(value: DateRangeValue): void
  onClear(): void
  onCancel(): void
  minDate?: Date
  maxDate?: Date
  disablePast?: boolean
  disableFuture?: boolean
}

function DateRangePicker(props: DateRangePickerProps) {
  const {
    value,
    onApply,
    onClear,
    onCancel,
    minDate: minDateProp,
    maxDate: maxDateProp,
    disablePast,
    disableFuture,
    className,
    ...divProps
  } = props

  const resolvedMinDate = disablePast
    ? laterDate(minDateProp, startOfDay(new Date()))
    : minDateProp

  const resolvedMaxDate = disableFuture
    ? earlierDate(maxDateProp, endOfDay(new Date()))
    : maxDateProp

  const [mode, setMode] = useState<DateRangeMode>(value?.mode ?? "within")
  const [start, setStart] = useState<Date | undefined>(
    value?.start ? new Date(value.start) : undefined,
  )
  const [end, setEnd] = useState<Date | undefined>(
    value?.end ? new Date(value.end) : undefined,
  )
  const [activeEdge, setActiveEdge] = useState<"start" | "end" | null>(null)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const heightRef = useRef(height)
  heightRef.current = height

  const scrollToMonthRef = useRef<((date: Date) => void) | null>(null)

  const callbacksRef = useRef({ onApply, onClear, onCancel })
  callbacksRef.current = { onApply, onClear, onCancel }

  const ctx = useMemo(
    () => ({
      mode,
      start,
      end,
      activeEdge,
      value,
      minDate: resolvedMinDate,
      maxDate: resolvedMaxDate,

      changeMode(newMode: DateRangeMode) {
        setMode(newMode)
        setStart(undefined)
        setEnd(undefined)
        setActiveEdge(null)
      },

      selectRange(range: { start?: Date; end?: Date } | undefined) {
        setStart(range?.start)
        setEnd(range?.end)
      },

      selectSingle(date: Date | undefined) {
        if (mode === "before") {
          setEnd(date)
        } else {
          setStart(date)
        }
      },

      setActiveEdge,

      commitStart(date: Date) {
        setStart(date)
      },

      commitEnd(date: Date) {
        setEnd(date)
      },

      apply() {
        const { onApply } = callbacksRef.current
        if (mode === "within" && start && end) {
          onApply({
            mode: "within",
            start: startOfDay(start).toISOString(),
            end: endOfDay(end).toISOString(),
          })
        } else if (mode === "before" && end) {
          onApply({ mode: "before", end: endOfDay(end).toISOString() })
        } else if (mode === "after" && start) {
          onApply({ mode: "after", start: startOfDay(start).toISOString() })
        }
      },

      applyPreset(preset: DateRangePreset) {
        const result = applyDateRangePreset(mode, preset)
        const newStart = result.start ? new Date(result.start) : undefined
        const newEnd = result.end ? new Date(result.end) : undefined
        setStart(newStart)
        setEnd(newEnd)
        const focus = newStart ?? newEnd
        if (focus) scrollToMonthRef.current?.(startOfMonth(focus))
      },

      clear() {
        setStart(undefined)
        setEnd(undefined)
        setActiveEdge(null)
        callbacksRef.current.onClear()
      },

      cancel() {
        callbacksRef.current.onCancel()
      },
    }),
    [mode, start, end, activeEdge, value, resolvedMinDate, resolvedMaxDate],
  )

  function handleResizePointerDown(e: React.PointerEvent) {
    e.preventDefault()
    const startY = e.clientY
    const startH = heightRef.current

    function onMove(ev: PointerEvent) {
      setHeight(Math.max(MIN_HEIGHT, startH + (ev.clientY - startY)))
    }

    function onUp() {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
    }

    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
  }

  return (
    <DateRangePickerProvider value={ctx}>
      <div
        className={cn("ui-date-picker", className)}
        style={{ height }}
        {...divProps}
      >
        <div className="ui-date-picker-columns">
          <DateRangePickerSidebar />
          <div className="ui-date-picker-main">
            <DateRangePickerBody
              scrollToMonthRef={scrollToMonthRef}
              initialStart={value?.start}
            />
            <DateRangePickerInputs />
          </div>
        </div>
        <DateRangePickerFooter />
        <div
          className="ui-date-picker-resize-handle"
          onPointerDown={handleResizePointerDown}
        />
      </div>
    </DateRangePickerProvider>
  )
}

export default DateRangePicker
